import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import dayjs from 'dayjs'
import { Alert, Button, Col, DatePicker, Empty, Input, InputNumber, Row, Select, Upload } from 'antd'
import { SearchOutlined, ToolOutlined, UploadOutlined } from '@ant-design/icons'
import { Titles } from '../../layouts/Titles'
import { SectionBadge, FieldBlock, ToggleField } from '../../components/SectionCard'
import i from '../../translations/i'
import controller from './super.controller'
import { SuperGuard } from './SuperGuard'

// Поля строки kssettings, которые раньше правили в /admin/setting/edit старого API.
// Блоки, порядок полей и варианты выпадающих списков — как там (templates/admin/setting/edit.html),
// поля, которых в old не было, — в конце своих блоков. Неописанные поля — в «Прочих полях»

type FieldType = 'text' | 'number' | 'bool' | 'date' | 'select' | 'languages' | 'logo' | 'readonly'

interface Option {
    value: any
    label: string
}

interface FieldConfig {
    name: string
    type?: FieldType
    options?: () => Option[]
}

const planOptions = () => ['first', 'second', 'third', 'fourth'].map((key, index) => ({ value: index, label: `${index} — ${i.t(`def:ratesName.${key}`)}` }))
const textOptions = (values: string[]) => () => values.map(value => ({ value, label: value }))

// Подписи вариантов — superAdmin:fields.options.<поле>.<значение>, без перевода показываем само значение
const codeOptions = (name: string, values: (string | number)[]) => () => values.map(value => {
    const key = `superAdmin:fields.options.${name}.${value}`
    return { value, label: i.exists(key) ? i.t(key) : `${value}` }
})

const LANGUAGES = ['ru', 'en', 'uk', 'pl', 'uz', 'tg', 'az', 'ky', 'ar']
const FONTS = ['helvetica', 'sanfrancisco', 'roboto', 'montserrat', 'gilroy']
const IMAGE_MODES = [1, 2]

const GROUPS: { key: string, fields: FieldConfig[] }[] = [
    { key: 'main', fields: [
        { name: 'bandl', type: 'readonly' },
        { name: 'name' },
        { name: 'mapaddressnumber', type: 'select', options: codeOptions('mapaddressnumber', [0, 1]) },
        { name: 'keymetric' },
        { name: 'typemetric', type: 'select', options: textOptions(['yandex', 'google', 'amplitude']) },
        { name: 'keymap' },
        { name: 'key_map_web' },
        { name: 'typemap', type: 'select', options: textOptions(['yandex', 'google']) },
        { name: 'keypay' },
        { name: 'typepay', type: 'select', options: textOptions(['yandex', 'sber', 'robokassa', 'psb', 'tinkoff', 'alfa', 'liqpay']) },
        { name: 'COUNTRY_CODE', type: 'select', options: textOptions(['RU', 'UA', 'KZ', 'PL', 'AE']) },
        { name: 'CURRENCY_CODE', type: 'select', options: textOptions(['RUB', 'USD', 'EUR', 'PLN', 'UAH', 'IQD', 'KZT', 'AED', 'TJS']) },
        { name: 'currency_lang_code', type: 'select', options: textOptions(['ru', 'uk', 'en', 'kk', 'ar_iq', 'pl', 'ar', 'tg']) },
        { name: 'currency_utf_8', type: 'select', options: textOptions(['ru_RU', 'uk_UA', 'en_US', 'kk_KZ', 'ar_IQ', 'pl_PL', 'ar']) },
        { name: 'gateway' },
        { name: 'gatewayMerchantId' },
        { name: 'link' },
        { name: 'googleid' },
        { name: 'appstoreid' },
        { name: 'tokenchat' },
        { name: 'metrikid' },
        { name: 'urlandroid' },
        { name: 'urlios' },
        { name: 'url_app_gallery' },
        { name: 'url_ru_store' },
        { name: 'deeplink' },
        { name: 'url' },
        { name: 'facebookid' },
        { name: 'pushkey' },
        { name: 'openapikey' },
        { name: 'app_language_main', type: 'select', options: textOptions(LANGUAGES) },
        { name: 'app_language', type: 'languages' },
        { name: 'linklogo', type: 'logo' },
        { name: 'logosite', type: 'logo' },
        { name: 'api_uri' },
        { name: 'wl_id', type: 'number' },
        { name: 'black_mark', type: 'bool' },
        { name: 'keytelegram' },
        { name: 'firebase_project' },
        { name: 'firebase_token' },
        { name: 'firebase_to' },
    ] },
    { key: 'app', fields: [
        { name: 'imagedisplaymodeincategories', type: 'select', options: codeOptions('imagedisplaymode', IMAGE_MODES) },
        { name: 'imagedisplaymodeinproducts', type: 'select', options: codeOptions('imagedisplaymode', IMAGE_MODES) },
        { name: 'addsearch', type: 'bool' },
        { name: 'addsort', type: 'bool' },
        { name: 'addfilter', type: 'bool' },
        { name: 'addfavorit', type: 'bool' },
        { name: 'variantheader', type: 'select', options: codeOptions('variantheader', [1, 2]) },
        { name: 'viewsimilar', type: 'bool' },
        { name: 'autopay', type: 'bool' },
        { name: 'viewwithproduct', type: 'bool' },
        { name: 'barcode', type: 'bool' },
        { name: 'lastviewproduct', type: 'bool' },
        { name: 'authorization', type: 'select', options: codeOptions('authorization', [0, 1, 2]) },
        { name: 'smsru_key' },
        { name: 'authorizationwhere', type: 'select', options: codeOptions('authorizationwhere', [0, 1]) },
        { name: 'viewproductvar', type: 'select', options: codeOptions('viewproductvar', [0, 1]) },
        { name: 'choicedelivery', type: 'select', options: codeOptions('choicedelivery', [0, 1, 2]) },
        { name: 'onbording' },
    ] },
    { key: 'copyright', fields: [
        { name: 'copyright', type: 'bool' },
        { name: 'copyright_cabinet', type: 'bool' },
        { name: 'splash', type: 'bool' },
        { name: 'copyright_dev' },
        { name: 'site_dev' },
        { name: 'mail_dev' },
        { name: 'datepayend', type: 'date' },
        { name: 'pricing_plan', type: 'select', options: planOptions },
    ] },
    { key: 'design', fields: [
        { name: 'android_btn_radius', type: 'number' },
        { name: 'android_input_radius', type: 'number' },
        { name: 'android_view_radius', type: 'number' },
        { name: 'ios_btn_radius', type: 'number' },
        { name: 'ios_input_radius', type: 'number' },
        { name: 'ios_view_radius', type: 'number' },
        { name: 'fontios', type: 'select', options: textOptions(FONTS) },
        { name: 'fontandroid', type: 'select', options: textOptions(FONTS) },
        { name: 'color_bonus_hex' },
        { name: 'color_bonus_rgb' },
    ] },
    { key: 'order', fields: [
        { name: 'dateorder', type: 'bool' },
        { name: 'dateorderpickup', type: 'bool' },
        { name: 'promocode', type: 'bool' },
        { name: 'unauthorizedorder', type: 'select', options: codeOptions('unauthorizedorder', [0, 1, 2]) },
        { name: 'timeorder', type: 'bool' },
        { name: 'viewintervaldelivery', type: 'bool' },
        { name: 'timestartdelivery', type: 'number' },
        { name: 'timeenddelivery', type: 'number' },
        { name: 'timeinterval', type: 'number' },
        { name: 'viewintervalpickup', type: 'bool' },
        { name: 'timestartpickup', type: 'number' },
        { name: 'timeendpickup', type: 'number' },
        { name: 'timeintervalpickup', type: 'number' },
        { name: 'timeassemble', type: 'number' },
        { name: 'timeassemblepickup', type: 'number' },
        { name: 'warehouses' },
    ] },
]

const HIDDEN = [
    'id',
    // Правятся на своих экранах: часовой пояс и «Следить за количеством» — Настройки → Проект,
    // остальное — Настройки → Заказы (белый список клиента в setting/edit)
    'timezone', 'productcount', 'ordercomment', 'deliveryattributes', 'ordering', 'ordering_description', 'cutlery', 'cutlery_description',
    // Бандлы приложений в сторах — отдельная таблица custom_bundles
    'custom_bundle_ios', 'custom_bundle_android',
    // Убраны по SK-1497. Домен витрины — своя таблица; position и лояльность с рефералкой никто не читает;
    // службы доставки и синхронизация (СБИС) — старые возможности old
    'url_online_store', 'position', 'loyaltysystem', 'referralsystem',
    'cdek', 'account', 'password', 'koddelivery', 'kodpoint', 'senderCityId', 'boxbery', 'tokenboxbery', 'pochta', 'pochtaWorld', 'zip',
    'novopochta', 'npapiKey', 'senderCityRef', 'ukrpochta', 'weight', 'length', 'width', 'height',
    'synchronization', 'synchrsystem', 'synchrparam', 'ymlupload',
]

const CONFIGURED = GROUPS.flatMap(group => group.fields.map(field => field.name))

const label = (name: string) => {
    const key = `superAdmin:fields.labels.${name}`
    return i.exists(key) ? i.t(key) : null
}

const FieldLabel = ({ name }: { name: string }) => {
    const text = label(name)

    return text
        ? <span>{text} <code style={{ fontWeight: 400, opacity: 0.6 }}>{name}</code></span>
        : <code>{name}</code>
}

// Флаги и коды приходят то числом, то строкой: выбираем вариант с тем же значением, иначе показываем как есть
const selectValue = (value: any, options: Option[]) => {
    if (value === null || value === undefined || value === '') return undefined

    return options.find(option => `${option.value}` === `${value}`)?.value ?? value
}

const languages = (value: any): string[] => {
    if (Array.isArray(value)) return value
    if (typeof value !== 'string' || !value) return []

    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const Logo = observer(({ name }: { name: string }) => {
    const value = controller.fields[name]
    const file = controller.files[name]

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {value && <img src={value} alt="" style={{ height: 40, maxWidth: 160, objectFit: 'contain' }} />}
                <Upload accept="image/*" showUploadList={false} beforeUpload={upload => {
                    controller.setFile(name, upload)
                    return false
                }}>
                    <Button icon={<UploadOutlined />}>{value ? i.t('def:actions.replace') : i.t('def:actions.upload')}</Button>
                </Upload>
            </div>
            {file
                ? <Input value={file.name} disabled />
                : <Input value={value ?? ''} onChange={e => controller.setField(name, e.target.value)} />
            }
        </div>
    )
})

const Field = observer(({ field }: { field: FieldConfig }) => {
    const value = controller.fields[field.name]
    const changed = controller.changedFields.includes(field.name)
    const style: React.CSSProperties = { width: '100%' }

    let input
    if (field.type === 'bool') {
        input = null
    } else if (field.type === 'number') {
        input = <InputNumber style={style} value={value === null || value === '' ? null : value} onChange={v => controller.setField(field.name, v)} />
    } else if (field.type === 'date') {
        input = <DatePicker
            style={style}
            format="DD.MM.YYYY"
            value={+value > 0 ? dayjs.unix(+value) : null}
            // Конец дня: тариф действует весь выбранный день
            onChange={date => controller.setField(field.name, date ? date.endOf('day').unix() : 0)}
        />
    } else if (field.type === 'select') {
        const options = field.options!()
        input = <Select style={style} value={selectValue(value, options)} options={options} onChange={v => controller.setField(field.name, v)} />
    } else if (field.type === 'languages') {
        // Как в old: все языки снять нельзя
        input = <Select
            style={style}
            mode="multiple"
            value={languages(value)}
            options={textOptions(LANGUAGES)()}
            onChange={(v: string[]) => v.length > 0 && controller.setField(field.name, v)}
        />
    } else if (field.type === 'logo') {
        input = <Logo name={field.name} />
    } else if (field.type === 'readonly') {
        input = <>
            <Input value={value ?? ''} disabled />
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>{i.t(`superAdmin:fields.readonly.${field.name}`)}</div>
        </>
    } else {
        input = <Input value={value ?? ''} onChange={e => controller.setField(field.name, e.target.value)} />
    }

    return (
        <div style={changed ? { boxShadow: '0 0 0 2px var(--primary-color)', borderRadius: 12 } : undefined}>
            {field.type === 'bool'
                ? <ToggleField label={<FieldLabel name={field.name} />} checked={!!+value} onChange={checked => controller.setField(field.name, checked ? 1 : 0)} />
                : <FieldBlock label={<FieldLabel name={field.name} />}>{input}</FieldBlock>
            }
        </div>
    )
})

const matches = (name: string, query: string) =>
    !query || name.toLowerCase().includes(query) || (label(name) ?? '').toLowerCase().includes(query)

const FieldsEditor = observer(() => {
    useEffect(() => {
        controller.getFields()
    }, [])

    if (!controller.fieldsLoaded) return <></>

    const query = controller.fieldsSearch.trim().toLowerCase()
    const present = (name: string) => Object.prototype.hasOwnProperty.call(controller.fields, name)

    const groups = [
        ...GROUPS.map(group => ({ key: group.key, fields: group.fields.filter(field => present(field.name)) })),
        {
            key: 'other',
            fields: Object.keys(controller.fields)
                .filter(name => !CONFIGURED.includes(name) && !HIDDEN.includes(name))
                .sort()
                .map(name => ({ name } as FieldConfig)),
        },
    ]
        .map(group => ({ ...group, fields: group.fields.filter(field => matches(field.name, query)) }))
        .filter(group => group.fields.length > 0)

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Alert type="info" showIcon message={i.t('superAdmin:fields.hint')} />

            <Row gutter={[16, 8]} align="middle">
                <Col xs={24} md={12} lg={8}>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder={i.t('superAdmin:fields.search')}
                        value={controller.fieldsSearch}
                        onChange={e => controller.fieldsSearch = e.target.value}
                    />
                </Col>
                {controller.changedFields.length > 0 &&
                    <Col>{i.t('superAdmin:fields.changed', { count: controller.changedFields.length })}</Col>
                }
            </Row>

            {groups.length === 0 && <Empty description={i.t('superAdmin:fields.empty')} />}

            {groups.map(group => (
                <div key={group.key} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <SectionBadge icon={<ToolOutlined />} text={i.t(`superAdmin:fields.groups.${group.key}`)} background="linear-gradient(135deg, #3f3d56, #63607f)" />
                    <Row gutter={[12, 12]}>
                        {group.fields.map(field => (
                            <Col xs={24} md={12} xl={8} key={field.name}>
                                <Field field={field} />
                            </Col>
                        ))}
                    </Row>
                </div>
            ))}
        </div>
    )
})

export const SuperProjectFields = observer(() => {
    return (
        <form onSubmit={e => controller.saveFields(e)}>
            <Titles
                title={i.t('superAdmin:hub.fields')}
                breadcrumb={[
                    { title: i.t('def:settings'), href: '/settings' },
                    { title: i.t('superAdmin:title') },
                    { title: i.t('superAdmin:hub.fields') },
                ]}
                buttons={controller.isSuper ? [{ title: i.t('def:actions.save'), htmlType: 'submit', type: 'primary' }] : []}
            />
            <SuperGuard>
                <FieldsEditor />
            </SuperGuard>
        </form>
    )
})
